import { useNavigate } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import { useOutletContext } from "react-router-dom";
import { useLinkedChildren } from "./useLinkedChildren";
import { ChildRow } from "./ChildRow";

// ---- Skeleton row (loading state) -------------------------------------------

function SkeletonRow() {
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 18px",
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        minHeight: 68,
      }}
    >
      <span
        className="zskel"
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: "var(--surface-sunk)",
          flex: "none",
        }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <span
          className="zskel"
          style={{
            height: 14,
            width: "40%",
            borderRadius: "var(--r-sm)",
            background: "var(--surface-sunk)",
          }}
        />
        <span
          className="zskel"
          style={{
            height: 11,
            width: "60%",
            borderRadius: "var(--r-sm)",
            background: "var(--surface-sunk)",
          }}
        />
      </div>
    </div>
  );
}

// ---- Empty state -------------------------------------------------------------

function EmptyState({
  title,
  body,
  onLinkChild,
  linkLabel,
}: {
  title: string;
  body: string;
  onLinkChild: () => void;
  linkLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 260,
        textAlign: "center",
        gap: 14,
      }}
    >
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 22,
          color: "var(--ink-deep)",
          margin: 0,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          color: "var(--ink-soft)",
          fontSize: 15.5,
          lineHeight: 1.6,
          maxWidth: 400,
          margin: 0,
        }}
      >
        {body}
      </p>
      <button
        onClick={onLinkChild}
        style={{
          marginTop: 6,
          minHeight: 48,
          padding: "0 22px",
          border: 0,
          borderRadius: "var(--r-pill)",
          background: "var(--brand)",
          color: "#fff",
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: 15,
          cursor: "pointer",
        }}
      >
        {linkLabel}
      </button>
    </div>
  );
}

// ---- Children screen ---------------------------------------------------------

export function Children() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const navigate = useNavigate();
  const tc = dict.service.children;
  const tl = dict.service.link;

  // Dual-mode (P1): fixtures stay synchronous for tests; live mode loads the
  // signed-in account's real children + summaries.
  const outlet = useOutletContext<{ userId?: string } | null>();
  const { linked, loading } = useLinkedChildren(outlet?.userId);

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 28,
            color: "var(--ink-deep)",
            margin: 0,
          }}
        >
          {tc.title}
        </h1>
        <button
          onClick={() => navigate("/link")}
          style={{
            minHeight: 44,
            padding: "0 18px",
            border: "1.5px solid var(--brand)",
            background: "transparent",
            color: "var(--brand)",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: "var(--r-pill)",
            cursor: "pointer",
          }}
        >
          {tl.title}
        </button>
      </div>

      {loading && (
        <div
          role="status"
          aria-label={tc.loading}
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {!loading && linked.length === 0 && (
        <EmptyState
          title={tc.emptyTitle}
          body={tc.emptyBody}
          onLinkChild={() => navigate("/link")}
          linkLabel={tl.title}
        />
      )}

      {!loading && linked.length > 0 && (
        <div
          role="list"
          aria-label={tc.title}
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          {linked.map((lc) => (
            <div key={lc.child.id} role="listitem">
              <ChildRow
                lc={lc}
                dict={dict}
                onClick={
                  lc.linkState === "active"
                    ? () => navigate(`/children/${lc.child.id}`)
                    : undefined
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
